import React, { memo, useEffect, useMemo, useCallback, useState, useRef } from 'react';
import { Icon, createUseStyles } from '@Sage/hoi-poi-ui';
import classnames from 'classnames';

import ScrollBar from './ScrollBar';

import styles from './styles';
const useStyles = createUseStyles(styles, 'QuickFilters');

const ScrollBarsCustom = memo(
    ({
        className,
        showVerticalOnHover,
        fadeTop,
        fadeBottom,
        fadeTopHeight,
        fadeBottomHeight,
        children,
        ...props
    }) => {
        const classes = useStyles();
        const scrollBarsRef = useRef(null);
        const innerRef = useRef(null);
        const [scrolledToLeft, setScrolledToLeft] = useState(true);
        const [scrolledToRight, setScrolledToRight] = useState(false);
        const timeoutRef = useRef(null);
        const timeoutAutoCancelRef = useRef(null);

        const handleMouse = useCallback(
            (e) => {
                e.stopPropagation();
                const childNodes = e.target.lastChild.childNodes;
                if (!childNodes.length) return;
                childNodes[1]?.classList?.toggle(classes.shown);
                childNodes[2]?.classList?.toggle(classes.shown);
            },
            [classes],
        );

        const scrollTo = useCallback((distance) => {
            const currentScrollDelta = innerRef.current?.getScrollLeft();
            innerRef.current?.scrollLeft(currentScrollDelta + distance);
            return true;
        }, []);

        const scrollToEdge = useCallback(
            (edge) => {
                clearInterval(timeoutRef.current);
                clearInterval(timeoutAutoCancelRef.current);
                timeoutAutoCancelRef.current = setInterval(function () {
                    const lastScroll = innerRef.current?.getScrollLeft();
                    scrollTo(40 * (edge > 0 ? 1 : -1));
                    if (lastScroll === innerRef.current?.getScrollLeft())
                        clearInterval(timeoutAutoCancelRef.current);
                }, 10);
            },
            [scrollTo],
        );

        const scrollOnOver = useCallback(
            (distance, time = 50) => {
                clearInterval(timeoutRef.current);
                timeoutRef.current = setInterval(function () {
                    scrollTo(distance);
                }, time);
                return true;
            },
            [scrollTo],
        );

        const onMouseOut = useCallback(() => {
            clearInterval(timeoutRef.current);
            return true;
        }, []);

        useEffect(() => {
            if (scrollBarsRef.current) {
                scrollBarsRef.current.addEventListener('mouseenter', handleMouse);
                scrollBarsRef.current.addEventListener('mouseleave', handleMouse);
            }
        }, [handleMouse]);

        const renderFadeTop = useMemo(() => {
            if (!fadeTop) return null;

            let style = {};
            if (fadeTopHeight) style['height'] = fadeTopHeight;

            return <div className={classes.topFade} style={style} />;
        }, [classes, fadeTop, fadeTopHeight]);

        const renderFadeBottom = useMemo(() => {
            if (!fadeBottom) return null;

            let style = {};
            if (fadeBottomHeight) style['height'] = fadeBottomHeight;

            return <div className={classes.bottomFade} style={style} />;
        }, [classes, fadeBottom, fadeBottomHeight]);

        const renderTrackHorizontal = useCallback(() => {
            return <div />;
        }, []);

        const renderThumbHorizontal = useCallback(() => {
            const rightScroll = scrolledToRight ? null : (
                <div
                    className={[classes.scrollButton, classes.buttonRight].join(' ')}
                    onClick={() => scrollToEdge(1)}
                    onMouseOver={() => scrollOnOver(10)}
                    onMouseOut={onMouseOut}
                >
                    <Icon name="chevronRight" />
                </div>
            );

            const leftScroll = scrolledToLeft ? null : (
                <div
                    className={[classes.scrollButton, classes.buttonLeft].join(' ')}
                    onClick={() => scrollToEdge(-1)}
                    onMouseOver={() => scrollOnOver(-10)}
                    onMouseOut={onMouseOut}
                >
                    <Icon name="chevronLeft" />
                </div>
            );

            return (
                <div className={classes.scrollContainer}>
                    {rightScroll}
                    {leftScroll}
                </div>
            );
        }, [scrolledToLeft, scrolledToRight, scrollToEdge, scrollOnOver, onMouseOut, classes]);

        const handleScrollUpdate = useCallback((values) => {
            const { scrollLeft, scrollWidth, clientWidth } = values;
            const roundedScrolledToLeft = Math.ceil(scrollLeft);
            setScrolledToLeft(roundedScrolledToLeft === 0);
            setScrolledToRight(roundedScrolledToLeft === scrollWidth - clientWidth);
        }, []);

        const rootClassName = classnames(classes.root, {
            [classes.withFade]: fadeTop || fadeBottom,
        });

        let classNamesScrollbars = classnames('', {
            [classes.showVerticalOnHover]: showVerticalOnHover,
        });

        return (
            <div ref={scrollBarsRef} className={rootClassName}>
                <ScrollBar
                    {...props}
                    autoHide={false}
                    hideTracksWhenNotNeeded={true}
                    className={classNamesScrollbars}
                    renderTrackHorizontal={renderTrackHorizontal}
                    renderThumbHorizontal={renderThumbHorizontal}
                    onUpdate={handleScrollUpdate}
                    ref={innerRef}
                >
                    <div className={classes.childrenRoot}>{children}</div>
                </ScrollBar>
                {renderFadeTop}
                {renderFadeBottom}
            </div>
        );
    },
);

export default ScrollBarsCustom;
